import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  Upload,
  Image as ImageIcon,
  Video,
  Trash2,
  MessageSquare,
  Calendar,
  Clock,
  Send,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Plus,
  ListChecks,
} from "lucide-react";
import { toast } from "sonner";

const TIERS = [
  { value: "free", label: "Free", color: "from-blue-500 to-cyan-500" },
  { value: "vip", label: "VIP", color: "from-amber-500 to-orange-500" },
  { value: "both", label: "Ambos", color: "from-purple-500 to-pink-500" },
];

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const TabButton = ({ active, onClick, icon: Icon, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
      active ? "bg-white text-black" : "bg-white/5 text-white/70 hover:text-white"
    }`}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </button>
);

const TierBadge = ({ tier }) => {
  const t = TIERS.find((x) => x.value === tier) || TIERS[2];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-gradient-to-r ${t.color} text-white`}
    >
      {t.label}
    </span>
  );
};

/* ---------- Library ---------- */
const LibraryTab = ({ username, getAuthHeaders }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [defaultTier, setDefaultTier] = useState("vip");
  const fileInputRef = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter
        ? `/api/admin/autopost/${username}/media?tier=${filter}`
        : `/api/admin/autopost/${username}/media`;
      const r = await fetch(url, { headers: { ...getAuthHeaders() } });
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) {
      toast.error("Error al cargar la biblioteca");
    } finally {
      setLoading(false);
    }
  }, [username, filter, getAuthHeaders]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let okCount = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tier", defaultTier);
      try {
        const r = await fetch(`/api/admin/autopost/${username}/media/upload`, {
          method: "POST",
          headers: { ...getAuthHeaders() },
          body: fd,
        });
        if (r.ok) okCount += 1;
      } catch (e) {
        // continue
      }
    }
    setUploading(false);
    toast.success(`${okCount}/${files.length} archivos subidos`);
    fetchItems();
  };

  const deleteItem = async (id) => {
    if (!window.confirm("¿Eliminar este archivo?")) return;
    const r = await fetch(`/api/admin/autopost/${username}/media/${id}`, {
      method: "DELETE",
      headers: { ...getAuthHeaders() },
    });
    if (r.ok) {
      toast.success("Eliminado");
      fetchItems();
    }
  };

  const updateTier = async (id, tier) => {
    const r = await fetch(`/api/admin/autopost/${username}/media/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ tier }),
    });
    if (r.ok) {
      fetchItems();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("")}
            className={`px-3 py-1.5 rounded-lg text-xs ${!filter ? "bg-white text-black" : "bg-white/10 text-white/70"}`}
          >
            Todas
          </button>
          {TIERS.map((t) => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              className={`px-3 py-1.5 rounded-lg text-xs ${filter === t.value ? "bg-white text-black" : "bg-white/10 text-white/70"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={defaultTier}
            onChange={(e) => setDefaultTier(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs"
          >
            {TIERS.map((t) => (
              <option key={t.value} value={t.value}>
                Subir como: {t.label}
              </option>
            ))}
          </select>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-media-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black font-medium text-sm hover:bg-white/90 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Subiendo..." : "Subir archivos"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-white/40 border border-dashed border-white/10 rounded-xl">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>No hay archivos en la biblioteca</p>
          <p className="text-xs mt-1">Sube fotos o videos para empezar</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {items.map((it) => (
            <div
              key={it.id}
              className="relative aspect-square rounded-lg overflow-hidden bg-white/5 group"
              data-testid={`media-item-${it.id}`}
            >
              {it.mediaType === "video" ? (
                <video src={it.mediaUrl} className="w-full h-full object-cover" muted />
              ) : (
                <img src={it.mediaUrl} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute top-1.5 left-1.5">
                <TierBadge tier={it.tier} />
              </div>
              {it.mediaType === "video" && (
                <div className="absolute top-1.5 right-1.5 bg-black/70 rounded p-1">
                  <Video className="w-3 h-3 text-white" />
                </div>
              )}
              <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black via-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between">
                <div className="flex gap-1">
                  {TIERS.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => updateTier(it.id, t.value)}
                      className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                        it.tier === t.value ? "bg-white text-black" : "bg-white/20 text-white"
                      }`}
                    >
                      {t.label[0]}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => deleteItem(it.id)}
                  className="p-1 text-red-400 hover:text-red-200"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {it.sentCount > 0 && (
                <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px]">
                  {it.sentCount}×
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- Captions ---------- */
const CaptionsTab = ({ username, getAuthHeaders }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [bulkText, setBulkText] = useState("");
  const [bulkTier, setBulkTier] = useState("both");
  const [aiTier, setAiTier] = useState("vip");
  const [aiCount, setAiCount] = useState(10);
  const [aiSeed, setAiSeed] = useState("");
  const [generating, setGenerating] = useState(false);
  const [aiResults, setAiResults] = useState([]);
  const [aiSelected, setAiSelected] = useState(new Set());

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const url = filter
      ? `/api/admin/autopost/${username}/captions?tier=${filter}`
      : `/api/admin/autopost/${username}/captions`;
    const r = await fetch(url, { headers: { ...getAuthHeaders() } });
    const d = await r.json();
    setItems(d.items || []);
    setLoading(false);
  }, [username, filter, getAuthHeaders]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const generateAI = async () => {
    setGenerating(true);
    setAiResults([]);
    setAiSelected(new Set());
    try {
      const r = await fetch(`/api/admin/autopost/${username}/captions/ai-suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          tier: aiTier,
          count: parseInt(aiCount) || 10,
          seed: aiSeed.trim(),
        }),
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        // Most likely: deployed env doesn't have this endpoint yet (returns HTML 404)
        const text = await r.text();
        if (r.status === 404 || text.startsWith("<!DOCTYPE")) {
          toast.error(
            "Esta función no está disponible en producción todavía. Haz Deploy en Emergent.",
            { duration: 8000 }
          );
        } else {
          toast.error(`Respuesta inesperada (HTTP ${r.status})`, { duration: 6000 });
        }
        return;
      }
      const d = await r.json();
      if (r.ok && d.captions?.length) {
        setAiResults(d.captions);
        setAiSelected(new Set(d.captions.map((_, i) => i)));
        toast.success(`✨ ${d.captions.length} captions generados`);
      } else {
        toast.error(d.detail || "No se pudo generar");
      }
    } catch (e) {
      toast.error(`Error de red: ${e?.message || "desconocido"}`, { duration: 6000 });
    } finally {
      setGenerating(false);
    }
  };

  const acceptAI = async () => {
    const chosen = aiResults.filter((_, i) => aiSelected.has(i));
    if (chosen.length === 0) {
      toast.error("Selecciona al menos uno");
      return;
    }
    const r = await fetch(`/api/admin/autopost/${username}/captions/accept`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ captions: chosen, tier: aiTier }),
    });
    if (r.ok) {
      const d = await r.json();
      toast.success(`${d.inserted} captions guardados`);
      setAiResults([]);
      setAiSelected(new Set());
      fetchItems();
    } else {
      toast.error("Error al guardar");
    }
  };

  const toggleAi = (i) => {
    setAiSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const addBulk = async () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return toast.error("Pega al menos una línea");
    const r = await fetch(`/api/admin/autopost/${username}/captions/bulk`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ texts: lines, tier: bulkTier }),
    });
    if (r.ok) {
      const d = await r.json();
      toast.success(`${d.inserted} captions agregados`);
      setBulkText("");
      fetchItems();
    }
  };

  const deleteCaption = async (id) => {
    const r = await fetch(`/api/admin/autopost/${username}/captions/${id}`, {
      method: "DELETE",
      headers: { ...getAuthHeaders() },
    });
    if (r.ok) fetchItems();
  };

  return (
    <div className="space-y-4">
      {/* AI generator */}
      <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4">
        <h3 className="text-white font-medium text-sm mb-3 flex items-center gap-2">
          ✨ Sugerencias con IA
          <span className="text-[10px] text-purple-200/60 font-normal">
            (sensual + persuasivo, en español)
          </span>
        </h3>

        {/* Seed input */}
        <div className="mb-3">
          <label className="text-[11px] text-purple-200/80 block mb-1 font-semibold">
            🌱 Palabra o tema semilla <span className="text-purple-200/40 font-normal">(opcional)</span>
          </label>
          <input
            type="text"
            value={aiSeed}
            onChange={(e) => setAiSeed(e.target.value)}
            placeholder="ej. lluvia · gym · lencería roja · viernes · piscina · aniversario..."
            data-testid="ai-seed-input"
            maxLength={200}
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs outline-none focus:border-purple-500/40 placeholder:text-white/30"
          />
          <p className="text-[10px] text-purple-200/50 mt-1">
            Si lo pones, todos los captions girarán alrededor de ese tema.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center mb-3">
          <select
            value={aiTier}
            onChange={(e) => setAiTier(e.target.value)}
            data-testid="ai-tier-select"
            className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs"
          >
            <option value="free">Para canal Free</option>
            <option value="vip">Para canal VIP</option>
            <option value="both">Ambos</option>
          </select>
          <input
            type="number"
            min="1"
            max="20"
            value={aiCount}
            onChange={(e) => setAiCount(e.target.value)}
            className="w-20 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs"
          />
          <span className="text-white/60 text-xs">captions</span>
          <button
            onClick={generateAI}
            disabled={generating}
            data-testid="ai-generate-btn"
            className="ml-auto px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {generating ? "Generando..." : "Generar con IA"}
          </button>
        </div>

        {aiResults.length > 0 && (
          <div className="space-y-2 mt-3">
            <p className="text-[11px] text-purple-200/80">
              Marca los que quieras guardar y pulsa "Aceptar":
            </p>
            {aiResults.map((c, i) => (
              <label
                key={i}
                className="flex items-start gap-3 p-2.5 bg-black/30 border border-white/10 rounded-lg cursor-pointer hover:border-purple-500/40"
              >
                <input
                  type="checkbox"
                  checked={aiSelected.has(i)}
                  onChange={() => toggleAi(i)}
                  className="mt-1 accent-pink-500"
                />
                <span className="text-white/90 text-xs whitespace-pre-line flex-1">{c}</span>
              </label>
            ))}
            <div className="flex gap-2 pt-2">
              <button
                onClick={acceptAI}
                data-testid="ai-accept-btn"
                className="flex-1 px-4 py-2 bg-white text-black font-bold rounded-lg text-xs"
              >
                Aceptar {aiSelected.size} caption{aiSelected.size !== 1 ? "s" : ""}
              </button>
              <button
                onClick={() => {
                  setAiResults([]);
                  setAiSelected(new Set());
                }}
                className="px-3 py-2 bg-white/10 text-white text-xs rounded-lg"
              >
                Descartar
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="text-white font-medium text-sm mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Agregar captions (uno por línea)
        </h3>
        <textarea
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          rows={4}
          placeholder="🔥 Nuevo contenido!\n📸 Mira esto, no te lo pierdas\n💎 Solo para mis fans VIP"
          data-testid="captions-bulk-input"
          className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder-white/30 outline-none focus:border-white/30 text-sm"
        />
        <div className="flex items-center gap-3 mt-3">
          <select
            value={bulkTier}
            onChange={(e) => setBulkTier(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs"
          >
            {TIERS.map((t) => (
              <option key={t.value} value={t.value}>
                Tier: {t.label}
              </option>
            ))}
          </select>
          <button
            onClick={addBulk}
            data-testid="captions-add-btn"
            className="px-4 py-2 bg-white text-black font-medium rounded-lg text-sm"
          >
            Agregar
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setFilter("")}
          className={`px-3 py-1.5 rounded-lg text-xs ${!filter ? "bg-white text-black" : "bg-white/10 text-white/70"}`}
        >
          Todos
        </button>
        {TIERS.map((t) => (
          <button
            key={t.value}
            onClick={() => setFilter(t.value)}
            className={`px-3 py-1.5 rounded-lg text-xs ${filter === t.value ? "bg-white text-black" : "bg-white/10 text-white/70"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {items.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-8">Sin captions</p>
          ) : (
            items.map((c) => (
              <div
                key={c.id}
                className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-lg group"
              >
                <TierBadge tier={c.tier} />
                <p className="flex-1 text-white/90 text-sm whitespace-pre-line">{c.text}</p>
                <button
                  onClick={() => deleteCaption(c.id)}
                  className="opacity-50 group-hover:opacity-100 text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- Schedule per tier ---------- */
const ScheduleCard = ({ username, schedule, getAuthHeaders, onUpdated, botUsername }) => {
  const [enabled, setEnabled] = useState(schedule.enabled);
  const [channelId, setChannelId] = useState(schedule.channelId || "");
  const [days, setDays] = useState(schedule.days || [0, 1, 2, 3, 4, 5, 6]);
  const [hours, setHours] = useState(schedule.hours || []);
  const [postsPerSlot, setPostsPerSlot] = useState(schedule.postsPerSlot || 1);
  const [postsPerDay, setPostsPerDay] = useState("");
  const [newHour, setNewHour] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [distributing, setDistributing] = useState(false);

  useEffect(() => {
    setEnabled(schedule.enabled);
    setChannelId(schedule.channelId || "");
    setDays(schedule.days || [0, 1, 2, 3, 4, 5, 6]);
    setHours(schedule.hours || []);
    setPostsPerSlot(schedule.postsPerSlot || 1);
    setVerifyResult(null);
  }, [schedule]);

  const autoDistribute = async () => {
    const n = parseInt(postsPerDay);
    if (!n || n < 1) {
      toast.error("Ingresa al menos 1 post por día");
      return;
    }
    setDistributing(true);
    try {
      const r = await fetch(
        `/api/admin/autopost/${username}/schedule/auto-distribute`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ postsPerDay: n }),
        }
      );
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        const text = await r.text();
        if (r.status === 404 || text.startsWith("<!DOCTYPE")) {
          toast.error(
            "Esta función no está disponible en producción todavía. Haz Deploy en Emergent.",
            { duration: 8000 }
          );
        } else {
          toast.error(`Respuesta inesperada (HTTP ${r.status})`, { duration: 6000 });
        }
        return;
      }
      const d = await r.json();
      if (r.ok && d.hours) {
        setHours(d.hours);
        toast.success(`✨ ${d.hours.length} horarios generados`);
      } else {
        toast.error(d.detail || "No se pudo generar");
      }
    } catch (e) {
      toast.error(`Error de red: ${e?.message || "desconocido"}`, { duration: 6000 });
    } finally {
      setDistributing(false);
    }
  };

  const removeChannel = async () => {
    if (!window.confirm(`¿Desvincular el canal ${schedule.tier.toUpperCase()}? Esto borra el link y los horarios.`)) return;
    const r = await fetch(`/api/admin/autopost/${username}/schedule/${schedule.tier}`, {
      method: "DELETE",
      headers: { ...getAuthHeaders() },
    });
    if (r.ok) {
      toast.success("Canal desvinculado");
      onUpdated && onUpdated();
    } else {
      toast.error("Error al desvincular");
    }
  };

  const verify = async () => {
    if (!channelId.trim()) {
      toast.error("Pega el link o ID del canal primero");
      return;
    }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await fetch(`/api/admin/autopost/verify-channel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ channelId: channelId.trim() }),
      });
      const d = await r.json();
      setVerifyResult(d);
      if (d.ok && d.canPost) {
        // Auto-update channelId to normalized form
        if (d.channelId) setChannelId(d.channelId);
        toast.success("Canal conectado correctamente");
      } else if (d.ok && !d.isAdmin) {
        toast.error("Canal encontrado, pero el bot aún no es administrador");
      } else if (d.ok && !d.canPost) {
        toast.error("El bot es admin pero no tiene permiso de publicar");
      } else {
        toast.error(d.error || "No se pudo verificar el canal");
      }
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setVerifying(false);
    }
  };

  const toggleDay = (d) => {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));
  };

  const addHour = () => {
    const m = newHour.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return toast.error("Formato HH:MM (ej. 14:30)");
    const h = String(parseInt(m[1])).padStart(2, "0");
    const min = m[2];
    if (parseInt(h) > 23 || parseInt(min) > 59) return toast.error("Hora inválida");
    const formatted = `${h}:${min}`;
    if (hours.includes(formatted)) return;
    setHours([...hours, formatted].sort());
    setNewHour("");
  };

  const removeHour = (h) => setHours(hours.filter((x) => x !== h));

  const save = async () => {
    setSaving(true);
    const r = await fetch(`/api/admin/autopost/${username}/schedule`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({
        tier: schedule.tier,
        enabled,
        channelId: channelId.trim(),
        days,
        hours,
        postsPerSlot: Math.max(1, Math.min(20, parseInt(postsPerSlot) || 1)),
        timezone: schedule.timezone || "America/Bogota",
      }),
    });
    setSaving(false);
    if (r.ok) {
      toast.success("Programación guardada");
      onUpdated && onUpdated();
    } else {
      toast.error("Error al guardar");
    }
  };

  const sendTest = async () => {
    if (!channelId.trim()) {
      toast.error("Pega el link del canal en el paso 2");
      return;
    }
    // Auto-save first so the test uses the latest channelId
    setTesting(true);
    try {
      const saveRes = await fetch(`/api/admin/autopost/${username}/schedule`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          tier: schedule.tier,
          enabled,
          channelId: channelId.trim(),
          days,
          hours,
          postsPerSlot: Math.max(1, Math.min(20, parseInt(postsPerSlot) || 1)),
          timezone: schedule.timezone || "America/Bogota",
        }),
      });
      if (!saveRes.ok) {
        toast.error("No se pudo guardar la configuración antes del test");
        setTesting(false);
        return;
      }

      const fd = new FormData();
      fd.append("tier", schedule.tier);
      const r = await fetch(`/api/admin/autopost/${username}/test`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
        body: fd,
      });
      if (r.ok) {
        toast.success("✅ Test enviado al canal", { duration: 5000 });
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Error al enviar test", { duration: 8000 });
      }
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setTesting(false);
    }
  };

  const tierMeta = TIERS.find((t) => t.value === schedule.tier);
  const addBotUrl = botUsername
    ? `https://t.me/${botUsername}?startchannel=true&admin=post_messages+edit_messages+delete_messages`
    : null;

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4" data-testid={`schedule-${schedule.tier}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-lg bg-gradient-to-br ${tierMeta.color} flex items-center justify-center`}
          >
            <Send className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-white font-bold text-lg">Canal {tierMeta.label}</h3>
        </div>
        <label className="inline-flex items-center cursor-pointer gap-2">
          <span className="text-xs text-white/60">{enabled ? "Activo" : "Pausado"}</span>
          <button
            type="button"
            onClick={() => setEnabled(!enabled)}
            className={`w-10 h-6 rounded-full relative transition-all ${enabled ? "bg-green-500" : "bg-white/20"}`}
          >
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${enabled ? "left-[18px]" : "left-0.5"}`} />
          </button>
        </label>
      </div>

      {/* Step 1: Add bot to channel */}
      {addBotUrl && (
        <a
          href={addBotUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`add-bot-${schedule.tier}`}
          className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/15 transition-colors group"
        >
          <div className="w-8 h-8 rounded-full bg-blue-500/30 flex items-center justify-center text-blue-200 font-bold text-sm flex-shrink-0">
            1
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-blue-100 text-sm font-medium">Agregar bot al canal</p>
            <p className="text-blue-200/60 text-[11px]">
              Abre Telegram y elige el canal. El bot se agregará como admin automáticamente.
            </p>
          </div>
          <span className="text-blue-200 text-xs font-semibold group-hover:translate-x-0.5 transition-transform">→</span>
        </a>
      )}

      {/* Step 2: Paste link + verify */}
      <div>
        <label className="text-xs text-white/50 block mb-1 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold">2</span>
          Pega el link o @username del canal
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={channelId}
            onChange={(e) => {
              setChannelId(e.target.value);
              setVerifyResult(null);
            }}
            placeholder="https://t.me/micanal o @micanal"
            data-testid={`channel-id-${schedule.tier}`}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30"
          />
          <button
            onClick={verify}
            disabled={verifying || !channelId.trim()}
            data-testid={`verify-${schedule.tier}`}
            className="px-4 py-2 bg-white text-black font-medium rounded-lg text-sm hover:bg-white/90 disabled:opacity-30 whitespace-nowrap"
          >
            {verifying ? "..." : "Verificar"}
          </button>
        </div>

        {/* Verify result */}
        {verifyResult && verifyResult.ok && (
          <div
            className={`mt-2 p-3 rounded-lg border text-xs ${
              verifyResult.canPost
                ? "bg-green-500/10 border-green-500/30 text-green-200"
                : "bg-amber-500/10 border-amber-500/30 text-amber-200"
            }`}
            data-testid={`verify-result-${schedule.tier}`}
          >
            <div className="flex items-center gap-2 mb-1">
              {verifyResult.canPost ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <Clock className="w-4 h-4" />
              )}
              <span className="font-semibold">{verifyResult.title || "Canal sin nombre"}</span>
              {verifyResult.memberCount != null && (
                <span className="opacity-60">· {verifyResult.memberCount} miembros</span>
              )}
            </div>
            {verifyResult.canPost ? (
              <p>Bot conectado y listo para publicar ✓</p>
            ) : !verifyResult.isAdmin ? (
              <p>El canal existe pero el bot aún no es administrador. Usa el botón "Agregar bot al canal" arriba.</p>
            ) : (
              <p>El bot es admin pero le falta el permiso "Publicar mensajes". Edita sus permisos en Telegram.</p>
            )}
          </div>
        )}
        {verifyResult && !verifyResult.ok && (
          <div className="mt-2 p-3 rounded-lg border bg-red-500/10 border-red-500/30 text-red-200 text-xs">
            <div className="flex items-center gap-2 mb-1 font-semibold">
              <XCircle className="w-4 h-4" />
              No se pudo verificar
            </div>
            <p className="opacity-90">{verifyResult.error}</p>
          </div>
        )}
      </div>

      <div>
        <label className="text-xs text-white/50 block mb-2">Días de la semana</label>
        <div className="flex flex-wrap gap-1.5">
          {DAYS.map((d, i) => (
            <button
              key={i}
              type="button"
              onClick={() => toggleDay(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                days.includes(i) ? "bg-white text-black" : "bg-white/10 text-white/50"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs text-white/50 block mb-2">Horarios de publicación (zona Bogotá)</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {hours.map((h) => (
            <span
              key={h}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-200 text-xs font-mono"
            >
              <Clock className="w-3 h-3" />
              {h}
              <button onClick={() => removeHour(h)} className="ml-1 hover:text-white">
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="time"
            value={newHour}
            onChange={(e) => setNewHour(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addHour())}
            placeholder="HH:MM"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono outline-none focus:border-white/30"
          />
          <button
            type="button"
            onClick={addHour}
            disabled={!newHour}
            className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg text-sm disabled:opacity-30"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[10px] text-white/30 mt-1.5">
          Ej: 08:00 = 8 AM · 14:30 = 2:30 PM · 20:00 = 8 PM
        </p>
      </div>

      {/* Auto-distribute: just N posts/day */}
      <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-3">
        <label className="text-xs text-purple-200 block mb-2 font-semibold">
          ⚡ Auto-distribuir horarios en el día
        </label>
        <div className="flex gap-2 items-center">
          <input
            type="number"
            min="1"
            max="48"
            value={postsPerDay}
            onChange={(e) => setPostsPerDay(e.target.value)}
            placeholder="ej. 12"
            data-testid={`posts-per-day-${schedule.tier}`}
            className="w-24 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30"
          />
          <span className="text-white/60 text-xs">posts / día</span>
          <button
            type="button"
            onClick={autoDistribute}
            disabled={distributing || !postsPerDay || parseInt(postsPerDay) < 1}
            data-testid={`auto-distribute-${schedule.tier}`}
            className="ml-auto px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-semibold rounded-lg hover:opacity-90 disabled:opacity-30"
          >
            {distributing ? "..." : "Generar horarios"}
          </button>
        </div>
        <p className="text-[10px] text-purple-200/60 mt-1.5">
          El sistema reparte tus N posts equitativamente entre 00:00 y 23:59. Reemplaza los horarios actuales.
        </p>
      </div>

      <div>
        <label className="text-xs text-white/50 block mb-1">Publicaciones por horario</label>
        <input
          type="number"
          min="1"
          max="20"
          value={postsPerSlot}
          onChange={(e) => setPostsPerSlot(e.target.value)}
          className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30"
        />
        <p className="text-[10px] text-white/30 mt-1">Cuántas fotos/videos enviar en cada hora programada.</p>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={save}
          disabled={saving}
          data-testid={`save-schedule-${schedule.tier}`}
          className="flex-1 py-2.5 bg-white text-black font-bold rounded-lg text-sm hover:bg-white/90 disabled:opacity-50"
        >
          {saving ? "Guardando..." : "Guardar"}
        </button>
        <button
          onClick={sendTest}
          disabled={testing || !channelId}
          data-testid={`test-${schedule.tier}`}
          className="px-4 py-2.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-200 font-medium rounded-lg text-sm disabled:opacity-30"
        >
          {testing ? "Enviando..." : "Enviar test"}
        </button>
        {schedule.channelId && (
          <button
            onClick={removeChannel}
            data-testid={`unlink-${schedule.tier}`}
            title="Desvincular canal"
            className="px-3 py-2.5 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-300 rounded-lg text-sm"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const ScheduleTab = ({ username, getAuthHeaders }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [botUsername, setBotUsername] = useState("");

  const fetch_ = useCallback(async () => {
    setLoading(true);
    const [sRes, botRes] = await Promise.all([
      fetch(`/api/admin/autopost/${username}/schedule`, { headers: { ...getAuthHeaders() } }),
      fetch(`/api/admin/autopost/bot-info`, { headers: { ...getAuthHeaders() } }),
    ]);
    const sData = await sRes.json();
    const botData = await botRes.json();
    setSchedules(sData.schedules || []);
    if (botData?.ok) setBotUsername(botData.username || "");
    setLoading(false);
  }, [username, getAuthHeaders]);

  useEffect(() => {
    fetch_();
  }, [fetch_]);

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {schedules.map((s) => (
        <ScheduleCard
          key={s.tier}
          username={username}
          schedule={s}
          getAuthHeaders={getAuthHeaders}
          onUpdated={fetch_}
          botUsername={botUsername}
        />
      ))}
    </div>
  );
};

/* ---------- Queue ---------- */
const QueueTab = ({ username, getAuthHeaders }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const url = statusFilter
      ? `/api/admin/autopost/${username}/queue?status=${statusFilter}`
      : `/api/admin/autopost/${username}/queue`;
    const r = await fetch(url, { headers: { ...getAuthHeaders() } });
    const d = await r.json();
    setPosts(d.posts || []);
    setLoading(false);
  }, [username, statusFilter, getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const removePost = async (id) => {
    const r = await fetch(`/api/admin/autopost/${username}/queue/${id}`, {
      method: "DELETE",
      headers: { ...getAuthHeaders() },
    });
    if (r.ok) load();
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("es-CO", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {[
            { v: "", l: "Todas" },
            { v: "pending", l: "Pendientes" },
            { v: "sent", l: "Enviadas" },
            { v: "failed", l: "Fallidas" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setStatusFilter(s.v)}
              className={`px-3 py-1.5 rounded-lg text-xs ${
                statusFilter === s.v ? "bg-white text-black" : "bg-white/10 text-white/70"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="p-2 text-white/50 hover:text-white rounded-lg hover:bg-white/5"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <p className="text-white/40 text-sm text-center py-8">Sin posts en cola</p>
      ) : (
        <div className="space-y-2">
          {posts.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-lg"
            >
              <div className="w-12 h-12 rounded overflow-hidden bg-white/10 flex-shrink-0">
                {p.mediaType === "video" ? (
                  <video src={p.mediaUrl} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={p.mediaUrl} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <TierBadge tier={p.tier} />
                  <span className="text-white/40 text-[10px]">{p.channelId}</span>
                </div>
                <p className="text-white text-sm truncate">{p.caption || <em className="text-white/30">Sin caption</em>}</p>
                <p className="text-white/40 text-[10px] mt-0.5 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(p.scheduledFor)}
                  {p.sentAt && <span> · enviado {formatDate(p.sentAt)}</span>}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                {p.status === "sent" && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                {p.status === "failed" && (
                  <span title={p.errorMsg || "Error"}>
                    <XCircle className="w-4 h-4 text-red-400" />
                  </span>
                )}
                {p.status === "pending" && <Clock className="w-4 h-4 text-white/40" />}
                <button
                  onClick={() => removePost(p.id)}
                  className="p-1 text-white/40 hover:text-red-400"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- Today (per creator) ---------- */
const TodayTab = ({ username, getAuthHeaders }) => {
  const [data, setData] = useState({ groups: [], totals: {}, date: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`/api/admin/autopost/today`, { headers: { ...getAuthHeaders() } });
    const d = await r.json();
    // Filter to this creator only
    const mine = (d.groups || []).filter((g) => g.creatorUsername === username);
    const totals = mine.reduce(
      (acc, g) => ({
        sent: acc.sent + g.sent,
        pending: acc.pending + g.pending,
        failed: acc.failed + g.failed,
      }),
      { sent: 0, pending: 0, failed: 0 }
    );
    setData({ date: d.date, groups: mine, totals });
    setLoading(false);
  }, [username, getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const fmtTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString("es-CO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-bold text-lg">Envíos de hoy</h3>
          <p className="text-white/50 text-xs">{data.date}</p>
        </div>
        <button
          onClick={load}
          className="p-2 text-white/50 hover:text-white rounded-lg hover:bg-white/5"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
          <div className="text-green-300 text-xs">Enviados</div>
          <div className="text-white text-2xl font-bold">{data.totals.sent || 0}</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <div className="text-amber-300 text-xs">Pendientes</div>
          <div className="text-white text-2xl font-bold">{data.totals.pending || 0}</div>
        </div>
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <div className="text-red-300 text-xs">Fallidos</div>
          <div className="text-white text-2xl font-bold">{data.totals.failed || 0}</div>
        </div>
      </div>

      {data.groups.length === 0 ? (
        <p className="text-white/40 text-sm text-center py-12">
          Sin actividad programada para hoy
        </p>
      ) : (
        data.groups.map((g) => (
          <div
            key={`${g.tier}-${g.channelId}`}
            className="bg-white/5 border border-white/10 rounded-xl p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TierBadge tier={g.tier} />
                <span className="text-white/60 text-xs font-mono">{g.channelId}</span>
              </div>
              <div className="flex gap-2 text-[11px]">
                <span className="text-green-300">✓ {g.sent}</span>
                <span className="text-amber-300">⏳ {g.pending}</span>
                {g.failed > 0 && <span className="text-red-300">✗ {g.failed}</span>}
              </div>
            </div>
            <div className="space-y-1.5">
              {g.posts.slice(0, 12).map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 text-xs text-white/70 py-1 border-b border-white/5"
                >
                  <span className="font-mono text-white/40 w-12">{fmtTime(p.scheduledFor)}</span>
                  {p.status === "sent" && <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />}
                  {p.status === "failed" && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                  {p.status === "pending" && <Clock className="w-3.5 h-3.5 text-amber-400" />}
                  <span className="truncate flex-1">
                    {p.caption || <em className="text-white/30">Sin caption</em>}
                  </span>
                </div>
              ))}
              {g.posts.length > 12 && (
                <p className="text-[10px] text-white/40 pt-1">
                  + {g.posts.length - 12} más en cola completa
                </p>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

/* ---------- Page ---------- */
const AdminAutoPost = () => {
  const { username } = useParams();
  const [activeTab, setActiveTab] = useState("library");
  const { getAuthHeaders } = useAuth();

  return (
    <AdminLayout>
      <div className="space-y-6">
        <Link
          to={`/admin/creadoras/${username}`}
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al perfil
        </Link>

        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
            Auto-Posting Telegram
          </h1>
          <p className="text-white/60">
            @{username} — gestión automática de canales Free y VIP
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <TabButton
            active={activeTab === "library"}
            onClick={() => setActiveTab("library")}
            icon={ImageIcon}
            testId="tab-library"
          >
            Biblioteca
          </TabButton>
          <TabButton
            active={activeTab === "captions"}
            onClick={() => setActiveTab("captions")}
            icon={MessageSquare}
            testId="tab-captions"
          >
            Captions
          </TabButton>
          <TabButton
            active={activeTab === "schedule"}
            onClick={() => setActiveTab("schedule")}
            icon={Calendar}
            testId="tab-schedule"
          >
            Programación
          </TabButton>
          <TabButton
            active={activeTab === "queue"}
            onClick={() => setActiveTab("queue")}
            icon={ListChecks}
            testId="tab-queue"
          >
            Cola
          </TabButton>
          <TabButton
            active={activeTab === "today"}
            onClick={() => setActiveTab("today")}
            icon={Send}
            testId="tab-today"
          >
            Hoy
          </TabButton>
        </div>

        <div>
          {activeTab === "library" && (
            <LibraryTab username={username} getAuthHeaders={getAuthHeaders} />
          )}
          {activeTab === "captions" && (
            <CaptionsTab username={username} getAuthHeaders={getAuthHeaders} />
          )}
          {activeTab === "schedule" && (
            <ScheduleTab username={username} getAuthHeaders={getAuthHeaders} />
          )}
          {activeTab === "queue" && (
            <QueueTab username={username} getAuthHeaders={getAuthHeaders} />
          )}
          {activeTab === "today" && (
            <TodayTab username={username} getAuthHeaders={getAuthHeaders} />
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAutoPost;
